import {
  fetchBidGroupsByProjectId,
  fetchBidGroupsPopulatedUncached,
  fetchInternalEstimateMarkupsByProjectId,
  fetchInternalEstimateMarkupsByProjectIdUncached,
  fetchScopeSheetDataByProjectId,
  fetchScopeSheetDataByProjectIdUncached,
  stageBidGroups,
  stageInternalMarkups,
  stageScopeSheet,
  watchContractingForClear,
} from "../../api";
import {
  Bid,
  BidGroup,
  BidStats,
  BidTabsData,
  InternalEstimateMarkup,
  ScopeAlternate,
  ScopeAlternateBid,
  ScopeTradeCleanupBid,
  ScopeUseGroup,
} from "../../interfaces/BidTabs";
import { computeUseGroupTotals } from "./scopeUseGroups";

export const refreshContractingDatasource = async (projectId: string) => {
  const notCleared = watchContractingForClear();
  const [bidGroupsResult, scopeResult, markupsResult] = await Promise.all([
    fetchBidGroupsPopulatedUncached(projectId),
    fetchScopeSheetDataByProjectIdUncached(projectId),
    fetchInternalEstimateMarkupsByProjectIdUncached(projectId),
  ]);
  if (!bidGroupsResult.success || !bidGroupsResult.data) return bidGroupsResult;
  if (!markupsResult.success) return { success: false as const, error: markupsResult.error };
  if (!bidGroupsResult.data.bidGroups.length) return bidGroupsResult;
  if (scopeResult.success && scopeResult.data) stageScopeSheet(projectId, scopeResult.data, notCleared);
  stageInternalMarkups(projectId, markupsResult.data ?? [], notCleared);
  return stageBidGroups(projectId, bidGroupsResult.data, notCleared);
};

export const getBidTabs = async (
  projectId: string
): Promise<{
  bidGroups: BidGroup[];
  markups: InternalEstimateMarkup[];
  scopeUseGroups: ScopeUseGroup[];
  scopeAlternates: ScopeAlternate[];
  scopeAlternateBids: ScopeAlternateBid[];
  scopeTradeCleanupBids: ScopeTradeCleanupBid[];
  bids: Bid[];
}> => {
  const [bidGroupsResult, markupsResult, scopeSheetResult] = await Promise.all([
    fetchBidGroupsByProjectId(projectId),
    fetchInternalEstimateMarkupsByProjectId(projectId),
    fetchScopeSheetDataByProjectId(projectId),
  ]);

  if (!bidGroupsResult.success) {
    throw bidGroupsResult.error;
  }
  const { bidGroups } = bidGroupsResult.data!;

  if (!markupsResult.success) {
    throw markupsResult.error;
  }
  const markups: InternalEstimateMarkup[] = markupsResult.data || [];

  // Scope data is non-critical — fall back gracefully
  const scopeData = scopeSheetResult.success ? scopeSheetResult.data : undefined;
  const scopeUseGroups: ScopeUseGroup[] = scopeData?.scope_use_groups || [];

  if (scopeData) {
    enrichBidGroupsWithScopeData(bidGroups, scopeData);
  }

  const scopeAlternates: ScopeAlternate[] = scopeData?.scope_alternates || [];
  const scopeAlternateBids: ScopeAlternateBid[] = scopeData?.scope_alternate_bid || [];

  const scopeTradeCleanupBids: ScopeTradeCleanupBid[] = scopeData?.scope_trade_cleanup_bid || [];
  const bids: Bid[] = scopeData?.bids || [];

  return { bidGroups, markups, scopeUseGroups, scopeAlternates, scopeAlternateBids, scopeTradeCleanupBids, bids };
};

/**
 * Attach to each bid group's selected bidder the use group, hold and allowance totals the
 * Contracting SOV reports, computed from the scope sheet the way the web app's bid groups grid
 * computes them.
 */
const enrichBidGroupsWithScopeData = (bidGroups: BidGroup[], scopeData: BidTabsData): void => {
  const scope_section_items = scopeData.scope_section_items || [];
  const scope_section_item_bid = scopeData.scope_section_item_bid || [];

  // Build lookup for holds/allowances: scope_section_item_id → bid_id → value
  // Only count bids with type "BP" or "SP" to match in-app totals
  const sectionItemBidLookup: Record<string, Record<string, number>> = {};
  for (const bid of scope_section_item_bid) {
    if (bid.deleted_at) continue;
    if (bid.type !== "BP" && bid.type !== "SP") continue;
    if (!sectionItemBidLookup[bid.scope_section_item]) sectionItemBidLookup[bid.scope_section_item] = {};
    sectionItemBidLookup[bid.scope_section_item][bid.bid] = parseFloat(bid.value || "0") || 0;
  }

  // Group hold/allowance-tagged scope section items by bid_group
  const holdItemsByBidGroup: Record<string, string[]> = {};
  const allowanceItemsByBidGroup: Record<string, string[]> = {};
  for (const item of scope_section_items) {
    if (item.header || item.deleted_at) continue;
    const tag = typeof item.tags === "string" ? item.tags.toUpperCase() : "";
    if (tag === "HOLD") {
      if (!holdItemsByBidGroup[item.bid_group]) holdItemsByBidGroup[item.bid_group] = [];
      holdItemsByBidGroup[item.bid_group].push(item.id);
    } else if (tag === "ALLOWANCE") {
      if (!allowanceItemsByBidGroup[item.bid_group]) allowanceItemsByBidGroup[item.bid_group] = [];
      allowanceItemsByBidGroup[item.bid_group].push(item.id);
    }
  }

  for (const bidGroup of bidGroups) {
    const selectedBidder = Object.values(bidGroup.totals).find((b) => b.selected);
    if (!selectedBidder) continue;

    selectedBidder.useGroups = computeUseGroupTotals(scopeData, bidGroup, selectedBidder);

    // Hold: sum of selected bidder's values for HOLD-tagged scope section items
    let holdTotal = 0;
    for (const itemId of holdItemsByBidGroup[bidGroup.id] || []) {
      holdTotal += sectionItemBidLookup[itemId]?.[selectedBidder.id] || 0;
    }
    selectedBidder.hold = holdTotal;

    // Allowance: same for ALLOWANCE-tagged items
    let allowanceTotal = 0;
    for (const itemId of allowanceItemsByBidGroup[bidGroup.id] || []) {
      allowanceTotal += sectionItemBidLookup[itemId]?.[selectedBidder.id] || 0;
    }
    selectedBidder.allowance = allowanceTotal;
  }
};

export const transformBidTabsToFlatRows = (
  bidGroupsData: BidGroup[],
  scopeUseGroups: ScopeUseGroup[] = [],
  scopeAlternates: ScopeAlternate[] = [],
  scopeAlternateBids: ScopeAlternateBid[] = [],
  scopeTradeCleanupBids: ScopeTradeCleanupBid[] = [],
  bids: Bid[] = []
): Record<string, any>[] => {
  const scopeUseGroupLabels = scopeUseGroups.reduce(
    (acc, sug) => {
      acc[sug.id] = sug.label;
      return acc;
    },
    {} as Record<string, string>
  );

  return bidGroupsData.map((bidGroup: BidGroup) => {
    const bidObjects: BidStats[] = Object.values(bidGroup.totals).filter(
      (bid: BidStats) => !(bid.is_carry && !bid.selected)
    );

    const bidLookup: Record<string, Bid> = {};
    for (const bid of bids) {
      if (bid.bid_group === bidGroup.id) bidLookup[bid.id] = bid;
    }

    const sortedBids: BidStats[] = bidObjects.sort((a: BidStats, b: BidStats) => {
      if (a.selected) return -1;
      if (b.selected) return 1;
      const aOrder = bidLookup[a.id]?.sort_order;
      const bOrder = bidLookup[b.id]?.sort_order;
      if (aOrder != null && bOrder != null && aOrder !== bOrder) return aOrder - bOrder;
      if (aOrder != null && bOrder == null) return -1;
      if (aOrder == null && bOrder != null) return 1;
      const aCreated = bidLookup[a.id]?.created_at || "";
      const bCreated = bidLookup[b.id]?.created_at || "";
      return bCreated.localeCompare(aCreated);
    });

    const selectedBid = sortedBids.find((bid) => bid.selected);
    const total = selectedBid ? selectedBid.grand_total : 0;

    const useGroupColumns: Record<string, number> = {};
    if (selectedBid?.useGroups) {
      for (const [scopeUgId, value] of Object.entries(selectedBid.useGroups)) {
        const label = scopeUseGroupLabels[scopeUgId] || scopeUgId;
        useGroupColumns[`${label}_$`] = value;
      }
    }

    const alternateColumns: Record<string, number> = {};
    if (selectedBid) {
      const bgAlternates = scopeAlternates.filter((alt) => !alt.bid_group);
      for (const alt of bgAlternates) {
        const altBid = scopeAlternateBids.find((sab) => sab.scope_alternate === alt.id && sab.bid === selectedBid.id);
        alternateColumns[`alt_${alt.label}`] = parseFloat(altBid?.value || "0") || 0;
      }
    }

    return {
      code: bidGroup.bid_package.code,
      label: bidGroup.label,
      total,
      budget: bidGroup.budget,
      hold: selectedBid?.hold || 0,
      allowance: selectedBid?.allowance || 0,
      trade_cleanup: selectedBid
        ? parseFloat(
            scopeTradeCleanupBids.find((stcb) => stcb.bid === selectedBid.id && !stcb.deleted_at)?.value || "0"
          ) || 0
        : 0,
      award: Object.values(bidGroup.totals).some((bid) => bid.award),
      projected_buy: bidGroup.projected_buy,
      buy_date: bidGroup.buy_date,
      buy_value: bidGroup.buy_value,
      ...sortedBids.reduce(
        (acc, bid, index) => {
          acc[`bid_${index + 1}_grand_total`] = bid.grand_total;
          acc[`bid_${index + 1}_name`] = bid.name;
          return acc;
        },
        {} as Record<string, any>
      ),
      ...useGroupColumns,
      ...alternateColumns,
      trade_estimator: bidGroup.trade_estimator?.full_name ?? "",
      created_at: bidGroup.created_at,
      updated_at: bidGroup.updated_at,
      updated_by: bidGroup.updated_by?.full_name ?? "",
      lock: bidGroup.lock,
      id: bidGroup.id,
    };
  });
};
